import {AfterViewInit, Component, OnDestroy, QueryList, ViewChildren,} from '@angular/core';
import {NgModel} from '@angular/forms';
import {Subscription} from 'rxjs';
import {UvsModuleModel} from '../../../../../../models/uvs-module.model';
import {GatewayType, isReadOnlyModuleType} from '../../../../../../interfaces/juconnGateway.response';
import {DataService} from '../../../../../../services/data.service';
import {ShareService} from '../../../../../../services/share.service';
import {ValidationService} from '../../../../../../services/validation.service';
import {debounceTime} from 'rxjs/operators';
import {SensorsOutsideTempModel} from '../../../../../../models/data-models/sensors-outside-temp-model';
import {GeneralGatewayModel} from '../../../../../../models/data-models/general-gateway-model';
import {OtmVerificationResult} from '../../../../../../interfaces/otm-verification.response';
import {DeviceDataService} from "../../../../../../services/device-data.service";

@Component({
  selector: 'app-install-sensors-control',
  templateUrl: './install-sensors-control.component.html',
  styleUrls: ['./install-sensors-control.component.scss'],
})
export class InstallSensorsControlComponent
  implements AfterViewInit, OnDestroy {
  public model: SensorsOutsideTempModel = new SensorsOutsideTempModel();
  public generalGatewayModel: GeneralGatewayModel = new GeneralGatewayModel();

  public configurationBeingSent = false;

  public otmVerificationLoading: { [modulId: string]: boolean } = {};
  public otmVerificationError: { [modulId: string]: string } = {};

  private inputSubscriptions: Subscription[] = [];
  private subscriptions: Subscription[] = [];

  @ViewChildren(NgModel) dynamicInputs: QueryList<NgModel>;

  constructor(
    private dataService: DataService,
    private shareService: ShareService,
    public validationService: ValidationService,
    private deviceDataService: DeviceDataService
  ) {
    this.subscriptions.push(
      this.shareService.sensorsOutsideTemp$.subscribe((data) => {
        this.model = data || new SensorsOutsideTempModel();
      }),
    );
    this.model =
      this.shareService.sensorsOutsideTemp || new SensorsOutsideTempModel();

    this.subscriptions.push(
      this.shareService.generalGateway$.subscribe((data) => {
        this.generalGatewayModel = data || new GeneralGatewayModel();
        this.filterModules();
      }),
    );
    this.generalGatewayModel =
      this.shareService.generalGateway || new GeneralGatewayModel();
    this.filterModules();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.inputSubscriptions.forEach((subscription) =>
      subscription.unsubscribe(),
    );
  }

  ngAfterViewInit(): void {
    this.dynamicInputs.forEach((input) => {
      this.inputSubscriptions.push(
        input.control.valueChanges.pipe(debounceTime(500)).subscribe(() => {
          this.shareService.sensorsOutsideTemp = this.model;
        }),
      );
    });

    // Subscribe to changes in the list of inputs
    // (it is called if some input is added or removed from the form/page,
    // for example, because of *ngIf condition changes)
    this.dynamicInputs.changes.subscribe(() => {
      // Unsubscribe from old subscriptions
      this.inputSubscriptions.forEach((subscription) =>
        subscription.unsubscribe(),
      );

      // Subscribe to new inputs
      this.dynamicInputs.forEach((input) => {
        this.inputSubscriptions.push(
          input.control.valueChanges.pipe(debounceTime(500)).subscribe(() => {
            this.shareService.sensorsOutsideTemp = this.model;
          }),
        );
      });
    });
  }

  isOutsideTemperatureModule(uvsModule: UvsModuleModel): boolean {
    return uvsModule.uvsType === GatewayType.OTM01 || uvsModule.uvsType === GatewayType.OTM02 || uvsModule.uvsType === GatewayType.OTM03;
  }

  areModulesSetUpCorrectly() {
    for (let i = 0; i < this.model.otmModules?.length; i++) {
      if (!this.model.otmModules[i].sensorTypeName) {
        return false;
      } else {
        this.model.otmModules[i].measuredValues.timestamp =
          new Date().toISOString();
      }
    }
    return true;
  }

  async loadVerification(module: UvsModuleModel) {
    this.otmVerificationLoading[module.modulId] = true;
    this.otmVerificationError[module.modulId] = null;
    try {
      const data = await this.dataService.getOtmVerificationData(
        this.shareService.getGatewayId(),
      );
      const v = data?.atmVerification ?? null;

      if (v) {
        module.otmVerification = v;
        module.sensorTypeName = v.sensorName ?? null;
        module.sensorParameters = {
          b: (v.sensorParameters as any)?.b ?? null,
          referenceResistance:
            (v.sensorParameters as any)?.referenceResistance ?? null,
        };
        if (v.measurements?.length > 0) {
          module.measuredValues = {
            ...module.measuredValues,
            outsideTemperature: v.measurements[0][0] ?? null,
            sensorResistance: v.measurements[0][1] ?? null,
            timestamp: v.timestamp ?? null,
          };
        }
        this.shareService.sensorsOutsideTemp = this.model;
      }
    } catch (e) {
      this.otmVerificationError[module.modulId] =
        (e as any)?.message || 'Unbekannter Fehler';
    } finally {
      this.otmVerificationLoading[module.modulId] = false;
    }
  }

  formatNumberDE(value: number, maxDecimals = 5): string {
    if (value === null || value === undefined || isNaN(value)) return '–';
    return value.toLocaleString('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: maxDecimals,
    });
  }

  formatTimestampDE(ts: string): string {
    if (!ts) return '–';
    const m = ts.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
    if (!m) return ts;
    const [, year, month, day, hour, minute] = m;
    return `${day}.${month}.${year}, ${hour}:${minute} Uhr`;
  }

  formatBoolDE(value: boolean): string {
    if (value === null || value === undefined) return '–';
    return value ? 'Ja' : 'Nein';
  }

  getBoolClass(value: boolean): string {
    if (value === null || value === undefined) return '';
    return value ? 'otm-bool--ja' : 'otm-bool--nein';
  }

  getMeasurementRowIndices(v: OtmVerificationResult): number[] {
    const count = Math.min(
      Math.max(v.measurements?.length || 0, v.errors?.length || 0),
      10,
    );
    return Array.from({length: count}, (_, i) => i);
  }

  getOtmWarning(module: UvsModuleModel): string | null {
    const v = module.otmVerification;
    if (!v) return null;
    if (v.successfullyValidated === true) {
      return null;
    }
    if (
      v.heatingHasTemperatureDisplay === false &&
      v.heatingTurnsOnForNegativeSimulatedTemperature === true
    ) {
      return null;
    }
    if (v.heatingHasTemperatureDisplay === false) {
      return 'Negative Temperatur Test durchführen';
    }
    return 'Keine plausiblen Werte gefunden, Verifikation erneut durchführen.';
  }

  isOtmVerificationValid(): boolean {
    return !(
      this.model.otmModules?.some((m) => this.getOtmWarning(m) !== null) ??
      false
    );
  }

  async sendModuleConfiguration() {
    const modulesRequest = (this.generalGatewayModel?.uvsModules || [])
      .filter((module) => module?.uvsType && !isReadOnlyModuleType(module.uvsType))
      .flatMap((module) => {
        return {
          type: module?.uvsType,
          address: module?.address,
          sensorTypeName: module?.sensorTypeName,
          sensorParameters: module?.sensorParameters,
          measuredValues: module?.measuredValues,
        };
      });

    const response = await this.dataService.postSetConfig(
      this.shareService.getGatewayId(),
      modulesRequest,
    );
  }

  public filterModules() {
    this.model.otmModules = [];


    const gatewayModules = this.generalGatewayModel?.uvsModules || [];
    if (gatewayModules.length > 0) {
      this.model.otmModules = gatewayModules.filter((uvsModule) =>
        this.isOutsideTemperatureModule(uvsModule),
      );
      this.model.otmModules.forEach((module) => {
        if (!module.measuredValues) {
          module.measuredValues = {
            outsideTemperature: null,
            temperatureDisplayedByHeatingSystem: null,
            sensorResistance: null,
            timestamp: null,
          };
        }
        if (!module.sensorParameters) {
          module.sensorParameters = {
            b: null,
            referenceResistance: null,
          };
        }
      });
    }

    if (!!this.deviceDataService.getDeviceData()?.deviceData?.uvsModule?.filter(module => module.moduleInfo.uvsType === GatewayType.HSC).length) {
      this.model.directControl.enabled = true
    }

    this.shareService.sensorsOutsideTemp = this.model;
  }
}
